import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Key, Copy, Check, AlertTriangle, ShieldOff, Lock, Eye, EyeOff } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { api, clearAuth } from '@/lib/api';

interface GeneratedToken {
  token:      string;
  name:       string;
  jti:        string;
  scope:      string;
  issued_at:  string;
  expires_at: string;
  expires_in: string;
}

const EXPIRES_OPTIONS = [
  { value: '30d',  label: '30 dias' },
  { value: '90d',  label: '90 dias' },
  { value: '365d', label: '1 ano (recomendado)' },
  { value: '2y',   label: '2 anos' },
];

export function Configuracoes() {
  return (
    <Layout title="Configurações">
      <p className="mb-6 text-sm text-text-muted">
        Configurações administrativas do sistema. Operações sensíveis — proceda com cuidado.
      </p>

      <div className="space-y-6">
        <AlterarSenhaSection />
        <ServiceTokensSection />
      </div>
    </Layout>
  );
}

// ─── Alterar senha ──────────────────────────────────────────────────────────

function AlterarSenhaSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [success,  setSuccess]  = useState(false);
  const [logoutIn, setLogoutIn] = useState<number | null>(null);

  // Validação local antes de bater no backend (alinhada com Joi do auth.schemas)
  const newMinOk    = newPassword.length >= 8;
  const newMaxOk    = newPassword.length <= 100;
  const matchOk     = newPassword.length > 0 && newPassword === confirmPassword;
  const differentOk = newPassword.length > 0 && newPassword !== currentPassword;
  const canSubmit   = !!currentPassword && newMinOk && newMaxOk && matchOk && differentOk;

  const change = useMutation({
    mutationFn: async () => {
      const r = await api.patch('/auth/me/password', { currentPassword, newPassword });
      return r.data;
    },
    onSuccess: () => {
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Boa prática de segurança: forçar relogin após troca, para invalidar
      // qualquer sessão antiga que ainda tenha o token velho na memória.
      let secs = 5;
      setLogoutIn(secs);
      const id = setInterval(() => {
        secs -= 1;
        setLogoutIn(secs);
        if (secs <= 0) {
          clearInterval(id);
          clearAuth();
          window.location.href = '/login';
        }
      }, 1000);
    },
  });

  const errorMsg = (() => {
    if (!change.isError) return null;
    const err: any = change.error;
    const code = err?.response?.data?.error?.code;
    if (code === 'WRONG_PASSWORD') return 'Senha atual incorreta.';
    return err?.response?.data?.error?.message || 'Erro ao alterar senha.';
  })();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Alterar senha</CardTitle>
        <Lock size={18} className="text-brand" />
      </CardHeader>

      <p className="mb-4 text-sm text-text-secondary">
        Mantém sua senha forte e única. Após alterar, sua sessão atual será encerrada e você precisa logar com a nova senha.
      </p>

      <div className="max-w-md space-y-3">
        <PasswordField
          label="Senha atual"
          value={currentPassword}
          onChange={setCurrentPassword}
          show={showCurrent}
          onToggle={() => setShowCurrent((v) => !v)}
          disabled={success}
        />

        <PasswordField
          label="Nova senha"
          hint={newPassword.length === 0 ? 'mínimo 8 caracteres' : (newMinOk ? `${newPassword.length} caracteres ✓` : `mínimo 8 caracteres (faltam ${8 - newPassword.length})`)}
          value={newPassword}
          onChange={setNewPassword}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          disabled={success}
        />

        <PasswordField
          label="Confirmar nova senha"
          hint={
            confirmPassword.length === 0
              ? undefined
              : matchOk
                ? 'senhas coincidem ✓'
                : 'senhas diferentes'
          }
          hintColor={confirmPassword.length === 0 ? undefined : matchOk ? 'text-green-400' : 'text-red-400'}
          value={confirmPassword}
          onChange={setConfirmPassword}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          disabled={success}
        />

        {newPassword.length > 0 && !differentOk && (
          <div className="rounded-lg bg-yellow-950/40 px-3 py-2 text-xs text-yellow-300">
            A nova senha deve ser diferente da atual.
          </div>
        )}

        <Button
          onClick={() => change.mutate()}
          disabled={!canSubmit || change.isPending || success}
          className="w-full"
        >
          {change.isPending ? 'Alterando…' : 'Alterar senha'}
        </Button>

        {errorMsg && (
          <div className="flex items-start gap-2 rounded-lg bg-red-950/40 px-3 py-2 text-xs text-red-300">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-2 rounded-lg bg-green-950/40 px-3 py-2 text-xs text-green-300">
            <Check size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Senha alterada com sucesso. Encerrando sessão em <strong>{logoutIn}s</strong> — você precisará logar com a nova senha.
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

interface PasswordFieldProps {
  label:      string;
  hint?:      string;
  hintColor?: string;
  value:      string;
  onChange:   (v: string) => void;
  show:       boolean;
  onToggle:   () => void;
  disabled?:  boolean;
}

function PasswordField({ label, hint, hintColor, value, onChange, show, onToggle, disabled }: PasswordFieldProps) {
  return (
    <div>
      <label className="mb-1 block text-xs text-text-secondary">
        {label}
        {hint && <span className={`ml-2 ${hintColor ?? 'text-text-muted'}`}>· {hint}</span>}
      </label>
      <div className="flex items-stretch gap-2">
        <Input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoComplete="off"
          className="flex-1"
        />
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          className="rounded-lg border border-border bg-bg-input px-3 text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary disabled:opacity-40"
          title={show ? 'Ocultar' : 'Mostrar'}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

// Escopos emissíveis. A lista de verdade mora no backend
// (`src/config/serviceScopes.js`), que recusa qualquer outro valor — aqui é só
// a tradução para quem lê a tela. Se divergirem, o erro aparece na hora de
// gerar, não silenciosamente na hora de usar.
const SCOPE_OPTIONS = [
  {
    value: 'service:monitor',
    label: 'service:monitor — leitura de KPIs',
    hint:  'Cron de relatórios e monitoramento externo. Não escreve nada.',
  },
  {
    value: 'admin:finances:write',
    label: 'admin:finances:write — lançar em Finanças',
    hint:  'Escrita PARCIAL em /financas. Não apaga mês e NÃO mexe no caixa — o caixa continua sendo só seu, conferido no extrato.',
  },
];

function ServiceTokensSection() {
  const [name, setName]           = useState('monitor-cron');
  const [expiresIn, setExpiresIn] = useState('365d');
  const [scope, setScope]         = useState('service:monitor');
  const [generated, setGenerated] = useState<GeneratedToken | null>(null);
  const [copied, setCopied]       = useState(false);
  const [revokeToken, setRevokeToken] = useState('');
  const [revokeMsg, setRevokeMsg]     = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: async () => {
      const r = await api.post('/admin/service-tokens', { name, expires_in: expiresIn, scope });
      return r.data.data as GeneratedToken;
    },
    onSuccess: (data) => {
      setGenerated(data);
      setCopied(false);
    },
  });

  const revoke = useMutation({
    mutationFn: async () => {
      const r = await api.post('/admin/service-tokens/revoke', { token: revokeToken });
      return r.data.data;
    },
    onSuccess: (data) => {
      setRevokeMsg(data.already_expired
        ? 'Token já estava expirado (nada a revogar).'
        : `Token revogado com sucesso · jti ${data.jti?.slice(0, 8)}…`);
      setRevokeToken('');
    },
    onError: () => {
      setRevokeMsg('Erro ao revogar — token inválido ou não é um service token.');
    },
  });

  const copyToken = async () => {
    if (!generated) return;
    await navigator.clipboard.writeText(generated.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service Tokens</CardTitle>
        <Key size={18} className="text-brand" />
      </CardHeader>

      <p className="mb-4 text-sm text-text-secondary">
        JWTs para automações externas (cron de relatórios, lançamentos de finanças, webhooks).
        Substituem o uso do script CLI. <strong className="text-text-primary">O escopo define o que o token pode fazer</strong> —
        e é o servidor que aplica o limite, não a boa vontade de quem usa.
      </p>

      {/* === Gerar === */}
      <div className="mb-6 rounded-lg border border-border bg-bg-input/40 p-4">
        <div className="mb-3 text-sm font-medium text-text-primary">Gerar novo token</div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-text-secondary">Nome (referência interna)</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex: monitor-cron" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-text-secondary">Validade</label>
            <select
              value={expiresIn}
              onChange={(e) => setExpiresIn(e.target.value)}
              className="h-10 w-full rounded-lg border border-border bg-bg-input px-3 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            >
              {EXPIRES_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3">
          <label className="mb-1 block text-xs text-text-secondary">Escopo</label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-bg-input px-3 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          >
            {SCOPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-text-secondary">
            {SCOPE_OPTIONS.find((o) => o.value === scope)?.hint}
          </p>
        </div>

        <Button className="mt-4" onClick={() => generate.mutate()} disabled={generate.isPending}>
          {generate.isPending ? 'Gerando…' : 'Gerar token'}
        </Button>

        {generated && (
          <div className="mt-4 space-y-3">
            <div className="flex items-start gap-3 rounded-lg bg-yellow-950/40 px-3 py-2 text-xs">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-yellow-400" />
              <div className="text-yellow-100">
                <strong>Copia o token agora.</strong> Ele só é mostrado UMA vez. Quando fechar essa página, não vai mais ser recuperado.
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs text-text-secondary">Token (Bearer)</label>
              <div className="flex items-stretch gap-2">
                <textarea
                  readOnly
                  value={generated.token}
                  rows={3}
                  className="flex-1 rounded-lg border border-brand bg-bg-input p-2 font-mono text-xs text-text-primary"
                />
                <Button onClick={copyToken} variant={copied ? 'outline' : 'default'} size="sm">
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copiado!' : 'Copiar'}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs text-text-secondary md:grid-cols-4">
              <Meta label="Nome"     value={generated.name} />
              <Meta label="Escopo"   value={generated.scope} />
              <Meta label="Validade" value={generated.expires_in} />
              <Meta label="Expira"   value={new Date(generated.expires_at).toLocaleDateString('pt-BR')} />
            </div>

            <div className="rounded-lg border border-border bg-bg-input/40 p-3 text-xs text-text-secondary">
              <div className="mb-1 font-medium text-text-primary">Como usar no cron:</div>
              <code className="block whitespace-pre-wrap break-all rounded bg-bg-input p-2 font-mono text-text-primary">
                {`Authorization: Bearer ${generated.token.slice(0, 30)}...`}
              </code>
            </div>
          </div>
        )}
      </div>

      {/* === Revogar === */}
      <div className="rounded-lg border border-border bg-bg-input/40 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-text-primary">
          <ShieldOff size={16} className="text-red-400" /> Revogar token
        </div>
        <p className="mb-3 text-xs text-text-secondary">
          Cole um service token pra adicionar à blacklist. Após revogado, o token deixa de funcionar imediatamente até expirar naturalmente.
        </p>
        <textarea
          rows={3}
          value={revokeToken}
          onChange={(e) => setRevokeToken(e.target.value)}
          placeholder="Cole o token aqui..."
          className="w-full rounded-lg border border-border bg-bg-input p-2 font-mono text-xs text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <div className="mt-3 flex items-center justify-between">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => revoke.mutate()}
            disabled={!revokeToken || revoke.isPending}
          >
            {revoke.isPending ? 'Revogando…' : 'Revogar'}
          </Button>
          {revokeMsg && (
            <span className={`text-xs ${revokeMsg.startsWith('Erro') ? 'text-red-400' : 'text-text-secondary'}`}>
              {revokeMsg}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-text-muted">{label}</div>
      <div className="text-text-primary">{value}</div>
    </div>
  );
}
